/* eslint-disable @typescript-eslint/no-explicit-any */
import { MouseEvent, useState } from 'react';
import {
    bundleIcon,
    TimerRegular,
    CalendarTodayFilled,
    CalendarTodayRegular,
    PeopleFilled,
    PeopleRegular,
} from '@fluentui/react-icons';
import { Button, Divider, makeStyles, Text, tokens } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useAccess } from '../../../../auth/useAccess';
import { BrandCard, Column, Row } from '../../../common';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { TimeTrackingEntryControl } from '../../../controls/TimeTrackingEntryControl';
import { useUtilities } from '../../../../utilities/useUtilities';
import { TimeTrackingTasksCard } from './TimeTrackingTasksCard';
import { TimeTrackingEntry, Case, UserAccount, UserRef, User } from '../../../../data/types';
import { useActiveTimer } from '../../../../data/services/useActiveTimer';
import { HorizontalWeekDaysSelectorControl } from '../../../controls/HorizontalSelectorControl';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { DropdownMenu } from '../../../controls/Menus/DropdownMenu';
import { Section } from '../../../common/Section';
import { CWTabList } from '../../../common/CWTabList';
import { useMediaSizes } from '../../../common/Styles/useMediaSizes';
import { UserDropdown } from '../../../controls/Comboboxes/UserDropdown';

const useStyles = makeStyles({
    emptyTimeEntries: {
        border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    datePicker: {
        visibility: 'collapse',
    },
});

enum TimeEntryTabs {
    byDay = 'byDay',
    myTime = 'myTime',
    allTime = 'allTime',
}
enum FilterOptions {
    week = 'Last 7 days',
    twoWeeks = 'Last 14 days',
    month = 'Last 30 days',
    all = 'All',
}

type BillableTimePageProps = {
    billable?: boolean;
};
export const BillableTimePage = (props: BillableTimePageProps) => {
    const { billable = false } = props;

    const { caseForm } = useCaseServicesContext()!;

    const caseObj = caseForm.values as Case;

    return (
        <>
            <Row gap='medium' flex='1'>
                <Column flex='1' gap='medium'>
                    <TimeEntryPanel billable={billable} caseObj={caseObj} />
                </Column>
                <Column width='auto' padding='px-2'>
                    <Divider vertical style={{ height: '100%' }} />
                </Column>
                <Column flex='1'>
                    <TimeTrackingTasksCard caseId={caseObj.caseId as string} billable={billable} />
                </Column>
            </Row>
        </>
    );
};

interface TimeEntryPanelProps {
    billable?: boolean;
    caseObj: Case;
}
const TimeEntryPanel = (props: TimeEntryPanelProps) => {
    const { billable, caseObj } = props;

    const { timeTrackingService } = useCaseServicesContext()!;

    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const utils = useUtilities();
    const mediaSizes = useMediaSizes();
    const { hoursForEntry } = useActiveTimer();
    const timeEntries = billable
        ? [...new Set([...(timeTrackingService?.billableTimeEntries || [])])]
        : [...new Set([...(timeTrackingService?.nonBillableTimeEntries || [])])];

    //State
    const [date, setDate] = useState<Date>(new Date());
    const [dateOpen, setDateOpen] = useState<boolean>(false);
    const [entryFilter, setEntryFilter] = useState<string>(FilterOptions.all);
    const [selectedTab, setSelectedTab] = useState<string>(TimeEntryTabs.byDay);
    const [selectedUser, setSelectedUser] = useState<User | UserRef | undefined>();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Access
    const canSeeAllTime = () => access.cases.canEditCaseAsAdmin(caseObj?.caseId);

    const myAssignedUser = () => {
        const myUser = caseObj.assignedUsers?.find((x) => x.userId === user.userId);
        return myUser;
    };

    //UI Events
    const clickToday = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setDate(new Date());
    };
    const onViewSelect = (tab: TimeEntryTabs) => {
        setSelectedTab(tab);
    };
    const onWeekDaySelect = (d: Date) => {
        setDate(d);
    };
    const onDatePickerOpenChange = (...e: any[]) => {
        const open = typeof e[0] === 'boolean' ? e[0] : e?.[1];
        if (typeof open === 'boolean') {
            setDateOpen(open);
        }
    };
    const onDatePickerSelect = (selectedDate: Date | null | undefined) => {
        setDate(selectedDate as Date);
        setDateOpen(false);
    };
    const onEntryFilterSelect = (option: FilterOptions) => {
        setEntryFilter(option);
    };

    const filterForToday = (entry: TimeTrackingEntry) => {
        return utils.date.isSameDay(entry.date!, date);
    };
    const filterEntries = (entry: TimeTrackingEntry) => {
        const today = new Date();
        const entryDate = new Date(entry.date!);

        // if (selectedTab != TimeEntryTabs.allTime && entry.userId != user.userId) return false;
        if (entry.userId != selectedUser?.userId && selectedUser) return false;

        if (entryFilter == FilterOptions.week) {
            const oneWeek = new Date();
            oneWeek.setDate(today.getDate() - 7);
            return entryDate >= oneWeek;
        } else if (entryFilter == FilterOptions.twoWeeks) {
            const twoWeek = new Date();
            twoWeek.setDate(today.getDate() - 14);
            return entryDate >= twoWeek;
        } else if (entryFilter == FilterOptions.month) {
            const month = new Date();
            month.setDate(today.getDate() - 31);
            return entryDate >= month;
        } else return true;
    };

    // Grouped timeEntries by date (e.g., '2024-06-10')
    const myTodayEntries = timeEntries.filter(filterForToday).filter((x) => x.userId == user.userId);
    const groupedEntries = timeTrackingService.groupEntriesByDate(timeEntries.filter(filterEntries));

    const totalHoursToday = myTodayEntries.reduce(
        (total: number, entry: TimeTrackingEntry) => total + hoursForEntry(entry),
        0,
    );

    //Api Functions passed to time entry control
    const onSubmitTimeEntry = async (entry?: TimeTrackingEntry) => {
        if (!!entry) {
            await timeTrackingService.submitEntryAsync(entry);
        }
    };
    const onDeleteTimeEntry = async (entry?: TimeTrackingEntry) => {
        if (!!entry) {
            await timeTrackingService.deleteTimeEntryAsync(entry);
        }
    };

    //Icon bundles
    const CalendarTodayBundle = bundleIcon(CalendarTodayFilled, CalendarTodayRegular);
    const PeopleBundle = bundleIcon(PeopleFilled, PeopleRegular);

    const viewAll = () => selectedTab == TimeEntryTabs.allTime;
    const viewMine = () => selectedTab == TimeEntryTabs.myTime;
    const viewByDay = () => selectedTab == TimeEntryTabs.byDay;

    return (
        <Column gap='medium'>
            <Column width='auto'>
                <CWTabList
                    selectedTab={selectedTab}
                    size='medium'
                    dataTestId='billable-time-view'
                    tabs={[
                        {
                            value: TimeEntryTabs.byDay,
                            label: 'View By Day',
                            icon: <CalendarTodayBundle />,
                            onClick: () => onViewSelect(TimeEntryTabs.byDay),
                        },
                        {
                            value: TimeEntryTabs.allTime,
                            label: 'View By User',
                            icon: <PeopleBundle />,
                            onClick: () => onViewSelect(TimeEntryTabs.allTime),
                            access: canSeeAllTime(),
                        },
                    ]}
                />
            </Column>
            <Row noGap>
                <Column padding='px-2' vAlign='middle'>
                    {!!billable && (
                        <Text>
                            My Billable Rate -{' '}
                            <Text weight='bold'>
                                {!!myAssignedUser()
                                    ? `${utils.formatCurrency(myAssignedUser()?.hourlyRate)} /hr`
                                    : 'No Rate'}
                            </Text>
                        </Text>
                    )}
                </Column>
                <Column align='right' width='auto' vAlign='middle' padding='px-2'>
                    <Row justifyContent='space-between' gap='medium'>
                        {(viewMine() || viewAll()) && (
                            <>
                                <DropdownMenu
                                    title='Filter'
                                    size='small'
                                    options={Object.values(FilterOptions).map((option) => ({
                                        label: option,
                                        value: option,
                                    }))}
                                    dataTestId='billable-time-filter-menu-button'
                                    onOptionChanged={(option) => onEntryFilterSelect(option as FilterOptions)}
                                    initialValue={entryFilter}
                                    width='fit-content'
                                />
                                <UserDropdown
                                    title='User'
                                    caseId={caseObj.caseId}
                                    onUserSelected={(user) => setSelectedUser(user)}
                                    includeClearOption
                                    size='small'
                                />
                            </>
                        )}
                    </Row>
                </Column>
            </Row>

            {/* View By Day Header */}
            {viewByDay() && (
                <Row noGap>
                    <Column flex='1' align='center' vAlign='middle'>
                        <Button
                            className={commonStyles.button.root}
                            size='small'
                            onClick={clickToday}
                            icon={<CalendarTodayBundle />}
                            data-testid='billable-time-today-button'
                        >
                            Today
                        </Button>
                    </Column>
                    <Column flex='2' gap='small' noGap align='center' vAlign='middle'>
                        <HorizontalWeekDaysSelectorControl date={date} onSelectDate={onWeekDaySelect} />

                        <Column height='0'>
                            <DatePicker
                                className={styles.datePicker}
                                open={dateOpen}
                                value={date}
                                onOpenChange={onDatePickerOpenChange}
                                onSelectDate={onDatePickerSelect}
                                placeholder='Select a date...'
                                data-testid='billable-time-date-picker'
                            />
                        </Column>
                    </Column>

                    <Column flex='1' align='center' vAlign='middle'>
                        <Text size={400} weight='bold'>
                            Hours: {totalHoursToday.toFixed(2)}
                        </Text>
                    </Column>
                </Row>
            )}
            {/* View My Time or All Time Header */}
            {(viewMine() || viewAll()) && (
                <Row noGap>
                    <Column flex='1' vAlign='middle' align='left' padding='pl-2'></Column>
                    <Column flex='1' vAlign='middle' align='right' padding='pr-3'>
                        <Text size={500} weight='semibold'>
                            Total Hrs:{' '}
                            {timeEntries
                                .filter(filterEntries)
                                .reduce((sum, entry) => sum + Number(entry.hours || 0), 0)
                                .toFixed(2)}
                        </Text>
                    </Column>
                </Row>
            )}

            <Column flex='1' gap='small'>
                <>
                    {viewByDay() && (
                        <>
                            {!!myTodayEntries.length ? (
                                <>
                                    {/* Time entries for the selected day */}
                                    <Column gap='medium' padding='pr-2' scroll>
                                        {myTodayEntries.map((entry) => (
                                            <TimeTrackingEntryControl
                                                key={entry?.entryId}
                                                entry={entry}
                                                showUserInfo={selectedTab == TimeEntryTabs.allTime}
                                                onModifyTimeEntry={onSubmitTimeEntry}
                                                onDeleteTimeEntry={onDeleteTimeEntry}
                                            />
                                        ))}
                                    </Column>
                                </>
                            ) : (
                                <Column flex='1' padding='px-3 py-3'>
                                    {/* No time entries for the day */}
                                    <Column
                                        className={styles.emptyTimeEntries}
                                        height='auto'
                                        width='100%'
                                        align='center'
                                        vAlign='middle'
                                        backgroundColor={tokens.colorNeutralBackground2}
                                        borderRadius='10px'
                                        padding='py-4'
                                    >
                                        <Text size={600}>
                                            No Time Entries for{' '}
                                            {utils.date.formatDate(
                                                date,
                                                {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                },
                                                true,
                                            )}
                                        </Text>
                                    </Column>
                                </Column>
                            )}
                        </>
                    )}
                    {(viewMine() || viewAll()) && !!timeEntries.length && (
                        <Column gap='medium' scroll padding='pl-2'>
                            {Object.entries(groupedEntries).map(([date, entries]) => (
                                <Section title={date} key={date}>
                                    {entries.map((entry) => (
                                        <TimeTrackingEntryControl
                                            showUserInfo={selectedTab == TimeEntryTabs.allTime}
                                            key={entry?.entryId}
                                            entry={entry}
                                            onModifyTimeEntry={onSubmitTimeEntry}
                                            onDeleteTimeEntry={onDeleteTimeEntry}
                                        />
                                    ))}
                                </Section>
                            ))}
                        </Column>
                    )}
                </>
            </Column>
        </Column>
    );
};
